import { Injectable } from '@angular/core';
import { CircleMarker, FeatureGroup, LatLng, Layer, Polyline } from 'leaflet';
import {
    AutopilotTrajectory,
    AutopilotCommand,
    AutopilotCommandEnum
} from '@mocca-technologies/model';

class ExploreNode {
    explored: boolean;
    command: AutopilotCommand;

    constructor(command: AutopilotCommand) {
        this.explored = false;
        this.command = command;
    }

    get latlng(): LatLng {
        const { point } = this.command;
        return new LatLng(point?.latitude, point?.longitude);
    }
}

@Injectable()
export class DrawTrajectoryService {
    readonly overlayColors = ['#FF595E', '#FFCA3A', '#8AC926', '#1982C4', '#6A4C93'];

    draw(apTrajectory: AutopilotTrajectory, overlays = new Map<string, FeatureGroup>()): any[] {
        // Clear older paths
        overlays.forEach((layer) => layer.clearLayers());
        overlays.clear();

        // Draw & notify parent to fit camera
        const nodes = apTrajectory.allCommandList.map((command) => new ExploreNode(command));
        return this.drawRoutes(nodes, overlays);
    }


    private drawRoutes(commands: ExploreNode[], overlays: Map<string, FeatureGroup>) {
        const upsertOverlay = (layer: Layer, stageLabel: string): void => {
            if (overlays.has(stageLabel)) {
                overlays.get(stageLabel).addLayer(layer);
            } else {
                overlays.set(stageLabel, new FeatureGroup([layer]));
            }
        };

        const getAdjacents = (target: ExploreNode): ExploreNode[] =>
            commands.filter(({ command }) => {
                const targetIdx = target.command.index;
                const isNext = command.nextCommandIdx === targetIdx;
                const isPrev = command.prevCommandIdx === targetIdx;
                const isPrevList = command.prevCommandIdxList?.includes(targetIdx);

                return isNext || isPrev || isPrevList;
            });

        // BFS among non-dependent nodes
        const leafs = commands.filter(
            ({ command }) =>
                command.apCommand === AutopilotCommandEnum.Initial ||
                command.apCommand === AutopilotCommandEnum.Takeoff
        );

        // Breadth-first search
        const queue = [...commands];
        while (queue.length) {
            const curr = queue.shift();
            if(curr.command.point){
                const label = curr.command.label;
                const stageLabel = curr.command.stageLabel || 'Operation';
                curr.explored = true;

                // Add marker for any command with labels
                if (label) {
                    const marker = new CircleMarker(curr.latlng, null).bindTooltip(label, {
                        sticky: true,
                        permanent: true,
                        className: 'only-text',
                        direction: 'bottom',
                        opacity: 1
                    });

                    upsertOverlay(marker, stageLabel);
                }

                // Propagate from leafs into adjacent nodes
                getAdjacents(curr).forEach((adjacent) => {
                    if (!adjacent.explored) {
                        // If it's not explored then push it into the queue
                        queue.unshift(adjacent);

                        if(adjacent.command.point){

                        }

                        if(adjacent.command.point) {
                            const layer = new Polyline([curr.latlng, adjacent.latlng]);
                            upsertOverlay(layer, stageLabel);
                        } else if(commands.at(adjacent.command.index + 1).command.point){
                            const layer = new Polyline([commands.at(curr.command.index).latlng, commands.at(adjacent.command.index + 1).latlng]);
                            upsertOverlay(layer, stageLabel);
                        } else if(commands.at(adjacent.command.index + 2).command.point){
                            const layer = new Polyline([commands.at(curr.command.index).latlng, commands.at(adjacent.command.index + 2).latlng]);
                            upsertOverlay(layer, stageLabel);
                        }

                    }
                });
            }
        }

        [...overlays.values()].forEach((layer, idx) =>
            layer.setStyle({ color: this.overlayColors[idx % 5] })
        );

        return [...overlays.values()];
    }
}
